import type { components } from "@solar/shared-types";
import type { MessageKey } from "../i18n/keys.ts";
import { HOMEOWNER_LOCALES } from "../i18n/negotiate.ts";

/**
 * "Send proposal" with Bona Fide Sign (components/SendProposal.tsx, the lead timeline; docs/features/esign.md): the
 * dialog's state (recipients in signing order with their language and identity check, message, order, expiry,
 * reminders, copies, "keep me updated"), its checks and buttons, where each signer's fields go (the same layout as
 * app/design/esign/fields.py; shared fixture), and the statuses in words. No React; tested in esign.test.ts.
 */

export type EsignStatus = "draft" | "sent" | "viewed" | "signed" | "declined" | "expired" | "cancelled";
export type EventStatus = EsignStatus | "resent" | "signed_copy_ready" | "bounced" | "changes_requested" | "corrected";
export type RecipientStatus = "pending" | "sent" | "viewed" | "signed" | "declined";
export type Role = "homeowner" | "co_signer";
export type SigningOrder = "sequential" | "parallel";
export type Verification = "none" | "email_otp";
export type SignedCopy = "none" | "finalising" | "ready" | "failed";
export type Tone = "ok" | "warn" | "danger" | "info" | undefined;
/** A tier B locale code (the contract's HomeownerLocaleCode). */
export type Language = components["schemas"]["HomeownerLocaleCode"];

export const PROVIDER = "Bona Fide Sign";
export const MAX_SIGNERS = 5;
export const MAX_CC = 5;
export const EXPIRY_CHOICES = [7, 14, 30, 60, 90] as const;
export const DEFAULT_MESSAGE = "Here is your solar proposal. Please review it and sign the acceptance page.";

/** The 50 homeowner languages (tier B of the locale registry, i18n/locales.json) a signer can get the signing page and
 * emails in, by their own name; the server maps each to Bona Fide Sign's code (app/design/esign/locales.py). */
export const SIGNING_LANGUAGES: readonly { code: Language; name: string }[] = HOMEOWNER_LOCALES.map((l) => ({ code: l.code as Language, name: l.name }));
export const DEFAULT_LANGUAGE: Language = "en";
const LANGUAGE_CODES = new Set<string>(SIGNING_LANGUAGES.map((l) => l.code));

/** A signer's language: the given one (the lead's, else the company's: the server's `default_locale`) when it is one of
 * the 50, else English. */
export function languageOr(code: string | null | undefined): Language {
  return code && LANGUAGE_CODES.has(code) ? (code as Language) : DEFAULT_LANGUAGE;
}

export function languageName(code: string): string {
  return SIGNING_LANGUAGES.find((l) => l.code === code)?.name ?? code;
}

/** The identity checks Bona Fide Sign offers, as message keys (catalog `esign`). */
export const VERIFICATION_CHOICES: readonly { value: Verification; label: MessageKey; hint: MessageKey }[] = [
  { value: "none", label: "esign.verification.none", hint: "esign.verification.noneHint" },
  { value: "email_otp", label: "esign.verification.emailOtp", hint: "esign.verification.emailOtpHint" },
];

/** Reminders to whoever's turn it is (up to three); null = off. */
export const REMINDER_CHOICES: readonly { days: number | null; label: MessageKey }[] = [
  { days: null, label: "esign.reminders.off" },
  { days: 1, label: "esign.reminders.daily" },
  { days: 2, label: "esign.reminders.every2" },
  { days: 3, label: "esign.reminders.every3" },
  { days: 7, label: "esign.reminders.weekly" },
];
export const DEFAULT_REMINDER_DAYS = 3;

// ------------------------------------------------------------------------------------------------ the dialog
export interface RecipientDraft {
  key: string;
  name: string;
  email: string;
  role: Role;
  locale: Language;
  verification: Verification;
}

export interface DialogState {
  recipients: RecipientDraft[];
  message: string;
  order: SigningOrder;
  expiresInDays: number;
  /** Days between reminders; null = off. */
  reminderDays: number | null;
  /** Addresses that get a copy of the signed proposal. */
  cc: string[];
  /** The sending user follows the envelope and is emailed as each person signs. */
  notifyMe: boolean;
  nextKey: number;
}

export function initialState(homeowner?: { name?: string | null; email?: string | null; locale?: string | null } | null, draft?: {
  recipients: { name: string; email: string; role: Role; locale?: string | null; verification?: Verification | null }[];
  message?: string | null;
  signing_order: SigningOrder;
  expires_in_days?: number;
  reminder_interval_days?: number | null;
  cc?: string[] | null;
  notify_me?: boolean | null;
} | null): DialogState {
  if (draft && draft.recipients.length) {
    return {
      recipients: draft.recipients.map((r, i) => ({
        key: `r${i}`, name: r.name, email: r.email, role: r.role, locale: languageOr(r.locale), verification: r.verification ?? "none",
      })),
      message: draft.message ?? "",
      order: draft.signing_order,
      expiresInDays: draft.expires_in_days ?? 30,
      reminderDays: draft.reminder_interval_days === undefined ? DEFAULT_REMINDER_DAYS : draft.reminder_interval_days,
      cc: draft.cc ?? [],
      notifyMe: !!draft.notify_me,
      nextKey: draft.recipients.length,
    };
  }
  return {
    recipients: [{ key: "r0", name: homeowner?.name ?? "", email: homeowner?.email ?? "", role: "homeowner", locale: languageOr(homeowner?.locale), verification: "none" }],
    message: DEFAULT_MESSAGE,
    order: "sequential",
    expiresInDays: 30,
    reminderDays: DEFAULT_REMINDER_DAYS,
    cc: [],
    notifyMe: false,
    nextKey: 1,
  };
}

export type DialogAction =
  | { type: "add" }
  | { type: "remove"; key: string }
  | { type: "edit"; key: string; field: "name" | "email"; value: string }
  | { type: "locale"; key: string; value: string }
  | { type: "verification"; key: string; value: Verification }
  | { type: "move"; key: string; by: -1 | 1 }
  | { type: "message"; value: string }
  | { type: "order"; value: SigningOrder }
  | { type: "expiry"; days: number }
  | { type: "reminders"; days: number | null }
  | { type: "cc"; values: string[] }
  | { type: "notifyMe"; value: boolean };

/** The homeowner stays first and cannot be removed; co-signers can be added (up to five signers), removed and moved
 * among themselves. */
export function reduceDialog(state: DialogState, action: DialogAction): DialogState {
  const list = state.recipients;
  switch (action.type) {
    case "add":
      if (list.length >= MAX_SIGNERS) return state;
      // a co-signer starts in the homeowner's language (often the same household)
      return { ...state, recipients: [...list, { key: `r${state.nextKey}`, name: "", email: "", role: "co_signer", locale: list[0]?.locale ?? DEFAULT_LANGUAGE, verification: "none" }], nextKey: state.nextKey + 1 };
    case "remove":
      return { ...state, recipients: list.filter((r) => r.key !== action.key || r.role === "homeowner") };
    case "edit":
      return { ...state, recipients: list.map((r) => (r.key === action.key ? { ...r, [action.field]: action.value } : r)) };
    case "locale":
      return { ...state, recipients: list.map((r) => (r.key === action.key ? { ...r, locale: languageOr(action.value) } : r)) };
    case "verification":
      return { ...state, recipients: list.map((r) => (r.key === action.key ? { ...r, verification: action.value } : r)) };
    case "move": {
      const i = list.findIndex((r) => r.key === action.key);
      const j = i + action.by;
      if (i < 1 || j < 1 || j >= list.length) return state; // the homeowner signs first
      const next = [...list];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return { ...state, recipients: next };
    }
    case "message":
      return { ...state, message: action.value };
    case "order":
      return { ...state, order: action.value };
    case "expiry":
      return { ...state, expiresInDays: Math.min(90, Math.max(1, Math.round(action.days))) };
    case "reminders":
      return { ...state, reminderDays: REMINDER_CHOICES.some((c) => c.days === action.days) ? action.days : DEFAULT_REMINDER_DAYS };
    case "cc":
      return { ...state, cc: action.values.map((v) => v.trim()).filter(Boolean) };
    case "notifyMe":
      return { ...state, notifyMe: action.value };
  }
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A copy address's problem (a message key, with `max`), or null: the CC field checks each address as it is added. */
export function ccProblem(value: string, current: string[] = []): MessageKey | null {
  const email = value.trim().toLowerCase();
  if (!EMAIL.test(email)) return "esign.copies.notEmail";
  if (current.some((c) => c.toLowerCase() === email)) return "esign.copies.twice";
  if (current.length >= MAX_CC) return "esign.copies.tooMany";
  return null;
}

/** Problems per recipient (by key) and of the copies (`cc`, a message key) and whether the dialog may be saved. */
export function validate(state: DialogState): { errors: Record<string, string>; ok: boolean } {
  const errors: Record<string, string> = {};
  const seen = new Set<string>();
  for (const r of state.recipients) {
    const email = r.email.trim().toLowerCase();
    if (!r.name.trim()) errors[r.key] = "Add the name";
    else if (!EMAIL.test(email)) errors[r.key] = email ? "Not an email address" : "Add the email address";
    else if (seen.has(email)) errors[r.key] = "Listed twice";
    seen.add(email);
  }
  const cc: MessageKey | null = state.cc.length > MAX_CC ? "esign.copies.tooMany" : state.cc.some((c) => !EMAIL.test(c.trim().toLowerCase())) ? "esign.copies.notEmail" : null;
  if (cc) errors.cc = cc;
  return { errors, ok: Object.keys(errors).length === 0 && state.recipients.length > 0 };
}

export interface Availability {
  provider_enabled: boolean;
  delivery_enabled: boolean;
  can_send: boolean;
  reason?: string | null;
}

/** The final button: "Send for signature", or disabled with the reason ("Bona Fide Sign is coming soon"); "Save
 * draft" always keeps the request. */
export function sendButton(availability: Availability | null | undefined, valid: boolean): { label: string; disabled: boolean; title: string } {
  if (!availability?.can_send) {
    const label = availability?.provider_enabled ? "Sending is off for now" : `${PROVIDER} is coming soon`;
    const title = availability?.reason ?? `${PROVIDER} is coming soon`;
    return { label, disabled: true, title: `${title}. Save the request as a draft; it is sent once signing is switched on.` };
  }
  return { label: "Send for signature", disabled: !valid, title: valid ? `Sends the proposal through ${PROVIDER}` : "Fix the recipients first" };
}

export function requestBody(state: DialogState, target: { public_quote_id?: string | null; design_id?: string | null }, send: boolean) {
  return {
    public_quote_id: target.public_quote_id ?? null,
    design_id: target.public_quote_id ? null : target.design_id ?? null,
    recipients: state.recipients.map((r) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role, locale: r.locale, verification: r.verification })),
    message: state.message.trim() || null,
    signing_order: state.order,
    expires_in_days: state.expiresInDays,
    reminder_interval_days: state.reminderDays,
    cc: state.cc,
    notify_me: state.notifyMe,
    send,
  };
}

/** "Expires 14 Nov 2026 (30 days)" from today. */
export function expiryText(days: number, today: Date = new Date()): string {
  const at = new Date(today.getTime() + days * 86_400_000);
  return `${at.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} (${days} days)`;
}

export function signerLabel(r: { role: Role }, index: number): string {
  return r.role === "homeowner" ? "Homeowner" : `Co-signer ${index}`;
}

// ------------------------------------------------------------------------------------------------ fields
export interface Field {
  signer: number;
  kind: "signature" | "initials" | "date";
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

type Anchor = number[];
const INITIALS_W = 52;
const INITIALS_H = 18;
const INITIALS_GAP = 6;
const EXTRA_MARGIN = 42;
const EXTRA_TOP = 120;
const EXTRA_BLOCK = 128;
/** 56 pt: room for Bona Fide Sign's seal QR code inside a signature box (esign/fields.py). */
const SIGNATURE_H = 56;
const EXTRA_LINE = 72;
const DATE_W = 120;
const DATE_H = 16;
const r2 = (v: number) => Math.round(v * 100) / 100;

/** The appended "Additional signatures" page's blocks (esign/fields.py `extra_page_blocks`). */
export function extraPageBlocks(pageSize: number[], count: number) {
  const [width, height] = [pageSize[0]!, pageSize[1]!];
  const sigW = r2((width - 2 * EXTRA_MARGIN) * 0.6);
  return Array.from({ length: count }, (_, k) => {
    const top = height - EXTRA_TOP - k * EXTRA_BLOCK;
    return { top, x: EXTRA_MARGIN + 14, y: top - EXTRA_LINE, width: sigW, date_x: r2(EXTRA_MARGIN + 14 + sigW + 24), date_w: DATE_W };
  });
}

/** Where each signer's fields go (esign/fields.py `layout`): the homeowner on the acceptance page, everyone's
 * initials on the terms heading, co-signers on the appended page. */
/** `company`: the last signer is the company's (Branding › Company signature): it signs and dates the company box
 * and does not initial. */
export function layoutFields(anchors: Record<string, Anchor>, pageCount: number, pageSize: number[], signers: number, company = false): { fields: Field[]; extraPage: boolean } {
  if (signers < 1) return { fields: [], extraPage: false };
  const fields: Field[] = [];
  const acceptance = "homeowner_signature" in anchors && "homeowner_date" in anchors;
  const companyBox = company && signers > 1 && "company_signature" in anchors && "company_date" in anchors;
  const people = company && signers > 1 ? signers - 1 : signers;
  const onExtra: number[] = [];
  for (let i = acceptance ? 1 : 0; i < signers; i++) if (!(companyBox && i === signers - 1)) onExtra.push(i);
  const box = (name: string, signer: number, kind: Field["kind"]) => {
    const [page, x, y, w, h] = anchors[name]!;
    fields.push({ signer, kind, page: page!, x: x!, y: y!, width: w!, height: h! });
  };
  if (acceptance) {
    box("homeowner_signature", 0, "signature");
    box("homeowner_date", 0, "date");
  }
  if (companyBox) {
    box("company_signature", signers - 1, "signature");
    box("company_date", signers - 1, "date");
  }
  if (anchors.terms_initials) {
    const [page, right, y] = anchors.terms_initials;
    for (let i = 0; i < people; i++) {
      fields.push({ signer: i, kind: "initials", page: page!, x: r2(right! - (i + 1) * INITIALS_W - i * INITIALS_GAP), y: y!, width: INITIALS_W, height: INITIALS_H });
    }
  }
  const blocks = extraPageBlocks(pageSize, onExtra.length);
  onExtra.forEach((signer, k) => {
    const b = blocks[k]!;
    fields.push({ signer, kind: "signature", page: pageCount + 1, x: b.x, y: b.y, width: b.width, height: SIGNATURE_H });
    fields.push({ signer, kind: "date", page: pageCount + 1, x: b.date_x, y: b.y, width: b.date_w, height: DATE_H });
  });
  return { fields: fields.map((f) => ({ ...f, x: r2(f.x), y: r2(f.y), width: r2(f.width), height: r2(f.height) })), extraPage: onExtra.length > 0 };
}

/** A field as CSS percentages of its page (the PDF's origin is bottom-left). */
export function fieldBox(f: Field, pageSize: number[]): { left: string; bottom: string; width: string; height: string } {
  const [w, h] = [pageSize[0]!, pageSize[1]!];
  const pct = (v: number) => `${r2(v)}%`;
  return { left: pct((f.x / w) * 100), bottom: pct((f.y / h) * 100), width: pct((f.width / w) * 100), height: pct((f.height / h) * 100) };
}

export const FIELD_LABELS: Record<Field["kind"], string> = { signature: "Sign", initials: "Init", date: "Date" };
export const FIELD_NAMES: Record<Field["kind"], string> = { signature: "Signature", initials: "Initials", date: "Date signed" };

// ------------------------------------------------------------------------------------------------ statuses
export const STATUS_LABELS: Record<EsignStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  signed: "Signed",
  declined: "Declined",
  expired: "Expired",
  cancelled: "Cancelled",
};

export function statusTone(status: EsignStatus): Tone {
  return status === "signed" ? "ok" : status === "declined" || status === "expired" ? "danger" : status === "draft" || status === "cancelled" ? undefined : "info";
}

export const RECIPIENT_LABELS: Record<RecipientStatus, string> = { pending: "Waiting", sent: "Sent", viewed: "Viewed", signed: "Signed", declined: "Declined" };

export function recipientTone(status: RecipientStatus): Tone {
  return status === "signed" ? "ok" : status === "declined" ? "danger" : status === "pending" ? undefined : "info";
}

/** Timeline entries that are not envelope statuses (a resent link, the signed copy, an email that did not arrive, a
 * signer's request for changes, a correction): their badge and line, as message keys (`{name}`: the recipient). */
export type KeyedEvent = Exclude<EventStatus, EsignStatus>;
export const EVENT_KEYS: Record<KeyedEvent, { badge: MessageKey; line: MessageKey; tone: Tone }> = {
  resent: { badge: "esign.timeline.resentBadge", line: "esign.status.resent", tone: "info" },
  signed_copy_ready: { badge: "esign.timeline.signedCopyBadge", line: "esign.timeline.signedCopyReady", tone: "ok" },
  bounced: { badge: "esign.timeline.bouncedBadge", line: "esign.timeline.bounced", tone: "danger" },
  changes_requested: { badge: "esign.timeline.changesRequestedBadge", line: "esign.timeline.changesRequested", tone: "warn" },
  corrected: { badge: "esign.timeline.correctedBadge", line: "esign.timeline.corrected", tone: "info" },
};

export function isEventKeyed(status: EventStatus): status is KeyedEvent {
  return Object.hasOwn(EVENT_KEYS, status);
}

export function eventTone(status: EventStatus): Tone {
  return isEventKeyed(status) ? EVENT_KEYS[status].tone : statusTone(status);
}

/** What the status line says about the signed copy once everyone signed (a message key), or null. */
export function signedCopyKey(copy: SignedCopy): MessageKey | null {
  return copy === "finalising" ? "esign.status.finalising" : copy === "failed" ? "esign.status.delayed" : null;
}

/** One line of the lead's timeline (the envelope's statuses; `resent` and `signed_copy_ready` come from EVENT_KEYS). */
export function timelineLine(e: { status: EsignStatus; recipient?: string | null }): string {
  const what = "Proposal";
  switch (e.status) {
    case "draft":
      return `${what} signing request saved as a draft`;
    case "sent":
      return `${what} sent for signature (${PROVIDER})`;
    case "viewed":
      return `${e.recipient ?? "A signer"} opened the proposal`;
    case "signed":
      return `${e.recipient ?? "Everyone"} signed the proposal`;
    case "declined":
      return `${e.recipient ?? "A signer"} declined to sign`;
    case "expired":
      return "The signing request expired";
    case "cancelled":
      return "The signing request was cancelled";
  }
}

/** Whether an envelope can still be cancelled. */
export function cancellable(status: EsignStatus): boolean {
  return status === "draft" || status === "sent" || status === "viewed";
}
